import { useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import type { ComboboxOption } from '@/components/ui/combobox'
import type { ShopPageRow } from '@/lib/api/shop-pages-api'
import { TextField } from '@/components/retail/form-bits'
import { Badge } from '@/components/ui/badge'
import { Combobox } from '@/components/ui/combobox'
import { Field } from '@/components/ui/field'
import { useDebounced } from '@/hooks/use-debounced'
import {
    useShopPages,
    useShopPagesInfinite,
} from '@/hooks/use-shop-pages-query'
import { m } from '@/lib/paraglide/messages'

const NAME = 'story_slug'
const SEARCH_DEBOUNCE_MS = 300
/** Cỡ trang tối đa của GET /shop-admin/pages (handler pageParams). */
const LOOKUP_PER_PAGE = 50

function storyOption(r: ShopPageRow): ComboboxOption {
    const draft = r.status !== 'published'
    return {
        value: r.slug,
        label: draft ? `${r.title} (${m.cmsPost_status_draft()})` : r.title,
        triggerLabel: r.title,
        render: () => (
            <div className="flex w-full min-w-0 items-center gap-2">
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{r.title}</div>
                    <div className="truncate font-mono text-xs text-muted-foreground">
                        {r.slug}
                    </div>
                </div>
                {draft && (
                    <Badge variant="outline" className="shrink-0">
                        {m.cmsPost_status_draft()}
                    </Badge>
                )}
            </div>
        ),
    }
}

/**
 * Ô "Câu chuyện dòng chè" của nhóm: chọn một mục Câu chuyện (shop_pages
 * kind=story) theo tiêu đề, lưu slug của mục đó vào `story_slug`. Không tải
 * được danh sách (vd thiếu quyền shop_content.read) → quay về ô nhập slug.
 */
export function StorySlugField() {
    const { control } = useFormContext<{ story_slug: string }>()
    const value = useWatch({ control, name: NAME })
    const [search, setSearch] = useState('')
    const q = useDebounced(search, SEARCH_DEBOUNCE_MS).trim()
    const list = useShopPagesInfinite({ kind: 'story', q: q || undefined })
    const rows = list.data?.pages.flatMap((p) => p.data ?? []) ?? []
    const listed = value ? rows.find((r) => r.slug === value) : undefined
    // Câu chuyện đang gắn có thể nằm ngoài trang đã tải: tra riêng theo slug
    // để nút hiện tiêu đề (hoặc báo slug không phải câu chuyện).
    const lookup = useShopPages(
        { kind: 'story', q: value, per_page: LOOKUP_PER_PAGE },
        { enabled: !!value && !list.isLoading && !listed },
    )
    const found =
        listed ?? lookup.data?.data?.find((r) => r.slug === value) ?? undefined
    // Server tìm q gần đúng (tiêu đề + slug chứa q), mới sửa trước: chỉ khẳng
    // định slug không phải câu chuyện khi đã xem hết kết quả tra.
    const lookupRows = lookup.data?.data ?? []
    const lookupComplete =
        (lookup.data?.meta?.total ?? lookupRows.length) <= lookupRows.length
    const missing =
        !!value &&
        !found &&
        lookup.isSuccess &&
        !lookup.isPlaceholderData &&
        lookupComplete

    if (list.isError) {
        return (
            <TextField
                name={NAME}
                label={m.shop_group_story()}
                hint={m.shop_group_story_hint_text()}
            />
        )
    }

    return (
        <Field name={NAME} label={m.shop_group_story()}>
            <Controller
                control={control}
                name={NAME}
                render={({ field }) => (
                    <Combobox
                        id={NAME}
                        options={rows.map(storyOption)}
                        value={field.value}
                        onChange={field.onChange}
                        onSearchChange={setSearch}
                        onOpenChange={(open) => !open && setSearch('')}
                        shouldFilter={false}
                        loading={list.isLoading}
                        onLoadMore={() => void list.fetchNextPage()}
                        hasMore={list.hasNextPage}
                        loadingMore={list.isFetchingNextPage}
                        allowClear
                        clearLabel={m.shop_group_story_clear()}
                        placeholder={m.shop_group_story_placeholder()}
                        searchPlaceholder={m.shop_group_story_search()}
                        emptyText={m.shop_group_story_empty()}
                        selectedOption={
                            field.value
                                ? found
                                    ? storyOption(found)
                                    : { value: field.value, label: field.value }
                                : undefined
                        }
                    />
                )}
            />
            {missing ? (
                <p className="text-xs text-destructive">
                    {m.shop_group_story_missing({ slug: value })}
                </p>
            ) : (
                <p className="text-xs text-muted-foreground">
                    {m.shop_group_story_hint()}
                </p>
            )}
        </Field>
    )
}
